/**
 * Shared recipient-validity rules.
 *
 * Two jobs, both pure so they can be unit-tested and used on the client:
 *
 * 1. `isUndeliverableRecipient` — addresses that can never receive our mail
 *    (machine/role senders such as `email@mailer.brand.com` or `noreply@…`,
 *    and internal release-test addresses). Sending to these produces a bounce
 *    every time, which then reads as a deliverability failure in the sweep.
 *    We block them before send and exclude them from bounce metrics.
 *
 * 2. `suggestEmailDomainCorrection` — one-edit typo guard so a mistyped
 *    domain ("southendcleanees.co.uk") is caught in the form instead of
 *    becoming a bounce.
 */

const ROLE_LOCALS = new Set([
  "noreply",
  "no-reply",
  "donotreply",
  "do-not-reply",
  "bounce",
  "bounces",
  "mailer-daemon",
  "postmaster",
  "abuse",
  "nobody",
]);

/** Subdomains only ever used by bulk senders, never by a human inbox. */
const SENDER_SUBDOMAINS = [
  "mailer.",
  "mail1.",
  "bounces.",
  "bounce.",
  "reply.",
  "em.",
  "sendgrid.",
];

/** Internal automated-test addresses on our own domain. */
export function isInternalTestRecipient(email: string | null | undefined): boolean {
  const e = (email ?? "").trim().toLowerCase();
  const [local = "", domain = ""] = e.split("@");
  if (!local || !domain) return false;
  if (domain !== "southendcleaners.co.uk") return false;
  return /^(release-e2e|e2e|smoke-test|sweep-test)([0-9]+)?([-+_].*)?$/.test(local);
}

/** Role/machine sender addresses that cannot receive transactional mail. */
export function isRoleSenderRecipient(email: string | null | undefined): boolean {
  const e = (email ?? "").trim().toLowerCase();
  const [local = "", domain = ""] = e.split("@");
  if (!local || !domain) return false;
  if (ROLE_LOCALS.has(local)) return true;
  return SENDER_SUBDOMAINS.some((p) => domain.startsWith(p));
}

/** Anything we should never send to, and never count as a real bounce. */
export function isUndeliverableRecipient(email: string | null | undefined): boolean {
  return isInternalTestRecipient(email) || isRoleSenderRecipient(email);
}

/** Short reason code for logs and skip records. */
export function undeliverableReason(email: string | null | undefined): string | null {
  if (isInternalTestRecipient(email)) return "internal_test_address";
  if (isRoleSenderRecipient(email)) return "role_sender_address";
  return null;
}

/* ------------------------------------------------------------------ */

const KNOWN_DOMAINS = [
  "southendcleaners.co.uk",
  "gmail.com",
  "googlemail.com",
  "hotmail.com",
  "hotmail.co.uk",
  "outlook.com",
  "live.co.uk",
  "yahoo.com",
  "yahoo.co.uk",
  "icloud.com",
  "btinternet.com",
  "sky.com",
  "aol.com",
  "me.com",
];

function editDistanceAtMost(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = new Array(b.length + 1).fill(0).map((_, i) => i);
  let prevPrev = new Array(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      // Adjacent transposition ("gmial" -> "gmail") counts as one edit.
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        curr[j] = Math.min(curr[j], prevPrev[j - 2] + 1);
      }
    }
    prevPrev = prev;
    prev = curr;
  }
  return prev[b.length];
}

/**
 * Returns the corrected address when the domain is one edit away from a
 * known-good domain, otherwise null. Never "corrects" an exact match.
 */
export function suggestEmailDomainCorrection(email: string | null | undefined): string | null {
  const e = (email ?? "").trim().toLowerCase();
  const at = e.lastIndexOf("@");
  if (at <= 0 || at === e.length - 1) return null;
  const local = e.slice(0, at);
  const domain = e.slice(at + 1);
  if (KNOWN_DOMAINS.includes(domain)) return null;
  for (const known of KNOWN_DOMAINS) {
    if (editDistanceAtMost(domain, known, 1) <= 1) return `${local}@${known}`;
  }
  return null;
}
